import { all, cancel, fork, call, put, select, takeLatest, takeEvery } from 'redux-saga/effects';
import _ from 'lodash';
import { delay } from 'redux-saga';
import config from 'config';
import * as http from 'utils/http';

import serialize from 'utils/serialize';
import history from 'modules/history';

import {
  setTotalsByDate,
  setTotalsByNoOwner,
  setCostByDate,
  setPlainData,
  resetPlainData,
  setGroupedData,
  setLoading,
  setError,
  setNextLink,
  setTotalsByDriver,
  setPatchData,
  setPlainAndGroupedData,
  setMounted,
  setLoadingNextPage,
  setReviewingJourney,
} from './duck';

import { getPlainData, getIsMounted, getNextPageLink } from './selectors';

import { TrackeableEvent } from 'redux/modules/analytics';
import { JOURNEYS_LOAD } from 'redux/modules/analytics/events';

import { filter$ } from 'redux/modules/jobs/effects';
import { setSidebarTab } from 'redux/modules/job-detail/duck';

import { takeAtomicUpdate } from 'redux/modules/realtime/atomic-updates';

import { groupJourneyData, isNotInActiveJobs, mapStatuses, mapFiltersForBackend } from './utils';
import { computeJourneys } from './computed';
import { DEFAULT_SIDEBAR_TAB } from 'redux/modules/journeys/constants';

const normalize = (data) => new Map(data.map((so) => [so.id, so]));

export const BASE_URLS = {
  queued: `${config.BASE_ROUTE}queued/`,
  unnasigned: `${config.BASE_ROUTE}jobs/unassigned/`,
  assigned: `${config.BASE_ROUTE}jobs/assigned/`,
  finished: `${config.BASE_ROUTE}jobs/finished/`,
};

// @effect Action
const openJourneyDetail$ = 'journeys/openJourneyDetail$';
const fetchMoreJourneys$ = 'journeys/fetchMoreJourneys$';
const visibilityChange$ = 'journeys/visibilityChange$';
const jobsMounted$ = 'journeys/jobsMounted$';
const jobsUnmounted$ = 'journeys/jobsUnmounted$';
const reviewJourney$ = 'journeys/reviewJourney$';

// @effect Creators
export const openJourneyDetail = (payload) => ({
  type: openJourneyDetail$,
  payload,
});

export const visibilityChange = (visible, filters) => ({
  type: visibilityChange$,
  payload: { visible, filters },
});

export const fetchMoreJourneys = (payload) => ({
  type: fetchMoreJourneys$,
  payload,
});

export const jobsMounted = (filters) => ({
  type: jobsMounted$,
  payload: filters,
});

export const jobsUnmounted = () => ({
  type: jobsUnmounted$,
  payload: {},
});

export const reviewJourney = ({ journeyId }) => ({
  type: reviewJourney$,
  payload: { journeyId },
});

// Workers
function* handleOpenJourneyDetail({ payload }) {
  const { id, fromList, listScrollTop, tab = '/jobs', sidebarTab = DEFAULT_SIDEBAR_TAB } = payload;

  yield put(setSidebarTab(sidebarTab));

  yield call(history.push, {
    pathname: `${tab}/${id}`,
    state: { fromList, listScrollTop },
  });
}

const loadJourneysTrackeableEvent = new TrackeableEvent();

function* handleTotalsByDate(filters) {
  yield put(setTotalsByDate({}));

  const backendFilters = yield call(mapFiltersForBackend, { ...filters, date: undefined });

  const { statuses } = backendFilters;

  if (isNotInActiveJobs(filters)) {
    return;
  }

  const queryStr = yield call(serialize, backendFilters);
  const statusesQueryStr = yield call(mapStatuses, statuses);

  try {
    const totalsByDate = yield call(
      http.get,
      `${config.API_URL}mercury/count/journeys_by_day/?${statusesQueryStr}&${queryStr}`
    );

    yield put(setTotalsByDate(totalsByDate));
  } catch (err) {
    console.error(err);
  }
}

function* handleTotalByNoOwners(filters) {
  yield put(setTotalsByNoOwner(0));

  const { date, statuses } = filters;

  if (isNotInActiveJobs(filters)) {
    return;
  }

  const statusesQueryStr = yield call(mapStatuses, statuses);
  const { dateField } = filters;
  const queryStr = yield call(serialize, { [dateField]: date ? date : undefined });

  try {
    const { count: totalsByNoOwner } = yield call(
      http.get,
      `${config.API_URL}mercury/count/journeys_with_no_owners/?${statusesQueryStr}&${queryStr}`
    );

    yield put(setTotalsByNoOwner(totalsByNoOwner));
  } catch (e) {
    console.error(e);
  }
}

function* handleTotalDriverIncome(filters) {
  yield put(setCostByDate({}));

  const backendFilters = yield call(mapFiltersForBackend, { ...filters, date: undefined });

  const { statuses, driver } = backendFilters;

  const requestNotNeeded = !driver;

  if (requestNotNeeded) {
    return;
  }

  const queryStr = yield call(serialize, backendFilters);
  const statusesQueryStr = yield call(mapStatuses, statuses);

  try {
    const costByDate = yield call(
      http.get,
      `${config.API_URL}mercury/count/journeys_income_by_day/?${statusesQueryStr}&${queryStr}`
    );

    yield put(setCostByDate(costByDate));
  } catch (err) {
    console.error(err);
  }
}

function* handleTotalByDriver(filters) {
  yield put(setTotalsByDriver({}));

  const { dateField, date, statuses: statusesFilters, driver } = filters;
  const backendFilters = yield call(mapFiltersForBackend, {
    dateField,
    statuses: statusesFilters,
    date,
    driver,
  });

  const { statuses } = backendFilters;

  const requestNotNeeded = yield call(isNotInActiveJobs, filters);

  if (requestNotNeeded) {
    return;
  }

  const queryStr = yield call(serialize, backendFilters);
  const statusesQueryStr = yield call(mapStatuses, statuses);

  try {
    const totalsByDriver = yield call(
      http.get,
      `${config.API_URL}mercury/count/journeys_by_driver/?${statusesQueryStr}&${queryStr}`
    );

    yield put(setTotalsByDriver(totalsByDriver));
  } catch (err) {
    console.error(err);
  }
}

function* handleTotals(ownFilters) {
  yield fork(handleTotalsByDate, ownFilters);
  yield fork(handleTotalByNoOwners, ownFilters);
  yield fork(handleTotalDriverIncome, ownFilters);
  yield fork(handleTotalByDriver, ownFilters);
}

let pollingRealtimeTask = null;
const pollingRealtimeTimeout = 120000;

function* handleFetchJourneys({ payload }) {
  if (pollingRealtimeTask) {
    yield cancel(pollingRealtimeTask);
  }

  const { nextPage } = payload;

  if (nextPage) {
    yield fork(handleFetchMoreJourneys, { payload });
    return;
  }

  const { triggerFetch } = payload;

  if (!triggerFetch) {
    return;
  }

  yield put(setError(false));
  yield put(setLoading(true));
  yield put(resetPlainData());
  yield put(setGroupedData({}));

  const { realtime, filters } = payload;
  const backendFilters = yield call(mapFiltersForBackend, filters);
  const queryStr = yield call(serialize, backendFilters);
  const { statuses } = backendFilters;

  const statusesQueryStr = yield call(mapStatuses, statuses);

  yield fork(handleTotals, filters);

  try {
    yield call(loadJourneysTrackeableEvent.start, JOURNEYS_LOAD);

    const { results: journeys, next } = yield call(
      http.get,
      `${config.API_URL}mercury/journeys/?${statusesQueryStr}&${queryStr}`
    );

    const computedJourneys = yield call(computeJourneys, journeys);
    const normalizedJourneys = yield call(normalize, computedJourneys);
    const groupedJourneys = yield call(groupJourneyData, computedJourneys, filters);

    yield put(setPlainData(normalizedJourneys));
    yield put(setGroupedData(groupedJourneys));

    yield put(setNextLink(next));

    yield call(loadJourneysTrackeableEvent.end, JOURNEYS_LOAD);

    yield put(setLoading(false));

    if (realtime) {
      pollingRealtimeTask = yield fork(pollingRealtime, filters);
    }
  } catch (err) {
    console.error('error', err);
    yield call(loadJourneysTrackeableEvent.end, JOURNEYS_LOAD, { error: err });
    yield put(setLoading(false));
    yield put(setError(err));
  } finally {
    yield put(setLoading(false));
  }
}

function* handleFetchMoreJourneys({ payload }) {
  const { nextPage, filters } = payload;

  try {
    yield put(setLoadingNextPage(true));
    yield call(loadJourneysTrackeableEvent.start, JOURNEYS_LOAD);

    const { results: newJourneys, next } = yield call(http.get, nextPage);

    const computedJourneys = yield call(computeJourneys, newJourneys);

    const plainData = yield select(getPlainData);
    const journeys = [...plainData.values(), ...computedJourneys];

    const normalizedJourneys = yield call(normalize, journeys, 'id');
    yield put(setPlainData(normalizedJourneys));

    const groupedJourneys = yield call(groupJourneyData, journeys, filters);

    yield put(setGroupedData(groupedJourneys));
    yield put(setNextLink(next));

    yield call(loadJourneysTrackeableEvent.end, JOURNEYS_LOAD);
  } catch (err) {
    console.error('error', err);
    yield call(loadJourneysTrackeableEvent.end, JOURNEYS_LOAD, { error: err });
  } finally {
    yield put(setLoadingNextPage(false));
  }
}

function* handleAtomicUpdate({ payload = {} }) {
  const { id } = payload || {};
  const currentData = yield select(getPlainData);

  if (!currentData) {
    return;
  }

  const journey = currentData.get(id);
  if (!journey) {
    return;
  }

  const newJourney = { ...journey, ...payload };
  yield put(setPatchData(newJourney));
}

const getNumberOfPagesFromNextLink = (nextLink) =>
  new URLSearchParams(nextLink.split('?')[1]).get('page');

const getPagesToRequest = (numberOfPages, filters) => {
  const urls = new Array(numberOfPages)
    .fill()
    .map((_, n) => {
      const backendFilters = mapFiltersForBackend({ ...filters, page: n + 1 });
      const queryStr = serialize(backendFilters);
      const { statuses } = backendFilters;

      const statusesQueryStr = mapStatuses(statuses);
      return `${queryStr}&${statusesQueryStr}`;
    })
    .map((queryStr) => `${config.API_URL}mercury/journeys/?${queryStr}`);

  return urls;
};

function* handleRealtime(filters) {
  const nextPageLink = yield select(getNextPageLink);
  const numberOfPages = parseInt(
    nextPageLink ? getNumberOfPagesFromNextLink(nextPageLink) - 1 : 1,
    10
  );

  const urls = yield call(getPagesToRequest, numberOfPages, filters);

  const allResults = yield all(urls.map((url) => call(http.get, url)));
  const journeys = _.flatten(allResults.map(({ results } = {}) => results || []));
  const computedJourneys = yield call(computeJourneys, journeys);
  const normalized = yield call(normalize, computedJourneys);
  const groupedData = yield call(groupJourneyData, journeys, filters);
  yield put(setPlainAndGroupedData({ plainData: normalized, groupedData }));
}

function* pollingRealtime(filters) {
  try {
    while (true) {
      yield call(delay, pollingRealtimeTimeout);
      yield fork(handleRealtime, filters);
    }
  } catch (err) {
    console.error(err);
  }
}

function* handleJobsMounted() {
  yield put(setMounted(true));
}

function* handleJobsUnmounted() {
  yield put(setMounted(false));
  if (pollingRealtimeTask) {
    yield cancel(pollingRealtimeTask);
  }
}

function* handleVisibilityChange({ payload: { visible, filters } }) {
  const isMounted = yield select(getIsMounted);

  if (!isMounted) {
    return;
  }

  if (!visible && pollingRealtimeTask) {
    yield cancel(pollingRealtimeTask);
    return;
  }

  if (visible) {
    pollingRealtimeTask = yield fork(pollingRealtime, filters);
    yield fork(handleRealtime, filters);
    return;
  }
}

function* handleReviewJourney({ payload: { journeyId } }) {
  if (!journeyId) return;

  yield put(setReviewingJourney({ journeyId, reviewing: true }));
  try {
    yield call(http.post, `${config.API_URL}mercury/journeys/${journeyId}/review/`);
  } catch (err) {
    console.error(err);
  } finally {
    yield put(setReviewingJourney({ journeyId, reviewing: false, reviewed: true }));
  }
}

// Watchers
export function* watchJourneys() {
  yield takeLatest(openJourneyDetail$, handleOpenJourneyDetail);
  yield takeLatest([filter$, fetchMoreJourneys$], handleFetchJourneys);

  yield takeLatest(visibilityChange$, handleVisibilityChange);

  yield takeLatest(jobsMounted$, handleJobsMounted);
  yield takeLatest(jobsUnmounted$, handleJobsUnmounted);
  yield takeEvery(reviewJourney$, handleReviewJourney);

  yield takeEvery(takeAtomicUpdate('journey:bulk_update_handler'), handleAtomicUpdate);
}
